// src/theme/components/card.js
import { cardAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(cardAnatomy.keys);

// Temel Card Stili (Tüm varyantlar için ortak olabilir)
const baseStyle = definePartsStyle({
  container: {
    borderRadius: "xl", // Daha modern: 16px (semantic token: radii.xl)
    bg: "bgPrimary",    // Semantic token
    color: "textPrimary", // Semantic token
    borderWidth: "1px",
    borderColor: "borderPrimary", // Semantic token
    overflow: "hidden",
    backgroundClip: "padding-box",
  },
  header: {
    paddingBottom: "4", // px, py gibi değerler için tema spacing scale'ini kullanır
    fontWeight: "semibold",
    fontSize: "xl", // Başlıklar için
  },
  body: {
    paddingTop: "4",
  },
  footer: {
    paddingTop: "4",
  },
});

// "Elevated" Varyantı (Modernleştirilmiş)
const elevated = definePartsStyle((props) => ({
  container: {
    bg: "bgPrimary",
    borderWidth: "1px",
    borderColor: "borderSecondary",
    borderRadius: "xl", // 16px
    boxShadow: "var(--chakra-shadows-cardElevated)", // semantic token (lg veya xl)
    backdropFilter: "saturate(180%) blur(20px)",
    backgroundImage: props.colorMode === 'dark'
      ? "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 100%)",
    // Faz 4.3++ · 'common' (4 prop) yerine sadece bg + border. Scroll sirasinda
    // istenmeyen transform/box-shadow animasyonlari onlenir.
    transitionProperty: "background-color, border-color",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    // _hover: { // İsteğe bağlı hover efekti
    //   transform: "translateY(-3px)",
    //   boxShadow: props.colorMode === 'dark' ? "0 12px 28px rgba(0,0,0,0.5)" : "0 10px 25px rgba(0,0,0,0.12)",
    // }
  },
}));

// "aiCard" Varyantı (Glassmorphism ve AI Teması)
const aiCard = definePartsStyle((props) => {
  const { colorMode } = props;
  const isDark = colorMode === 'dark';

  return {
    container: {
      // Glassmorphism etkisi için yarı saydam arka plan ve bulanıklık
      // bg: isDark ? "rgba(30, 41, 59, 0.65)" : "rgba(255, 255, 255, 0.75)", // gray.800 ve white için örnek
      bg: isDark ? "var(--chakra-colors-gray-800_60Alpha)" : "var(--chakra-colors-whiteAlpha-800)", // colors.js'de tanımlı olmalı
      backdropFilter: "blur(16px) saturate(180%)",
      WebkitBackdropFilter: "blur(16px) saturate(180%)", // Safari için
      borderWidth: "1px",
      // Hafif renkli ve yarı saydam kenarlık (accent rengiyle)
      borderColor: isDark ? "var(--chakra-colors-brand-300-alpha-30)" : "var(--chakra-colors-brand-500-alpha-20)",
      borderRadius: "xl", // 16px
      boxShadow: isDark ? "0px 10px 25px rgba(0,0,0,0.4)" : "0px 8px 20px rgba(0,100,200,0.15)",
      // Faz 4.3++ · transform kaldirildi (scroll'da gereksiz GPU layer hareketi)
      transitionProperty: "background-color, border-color, box-shadow",
      transitionDuration: "var(--motion-duration-base)",
      transitionTimingFunction: "var(--motion-ease-apple)",
      // İsteğe bağlı: Üzerine gelince hafif bir "glow" efekti
      // _hover: {
      //   borderColor: isDark ? "var(--chakra-colors-brand-300-alpha-50)" : "var(--chakra-colors-brand-500-alpha-30)",
      //   boxShadow: "var(--chakra-shadows-aiGlowAccentSm)",
      // }
    },
    header: {
        borderBottomWidth: "1px",
        borderColor: isDark ? "var(--chakra-colors-brand-300-alpha-30)" : "var(--chakra-colors-brand-500-alpha-20)",
        pb: 3,
        mb: 3,
    },
  };
});

// "glass" Varyantı (Dock tarzı glassmorphism yüzey)
const glass = definePartsStyle(() => ({
  container: {
    bg: 'headerBg',
    borderWidth: '1px',
    borderColor: 'borderPrimary',
    borderRadius: '2xl',
    boxShadow: 'cardElevated',
    backdropFilter: 'saturate(180%) blur(28px)',
    WebkitBackdropFilter: 'saturate(180%) blur(28px)',
    transitionProperty: 'background-color, border-color, box-shadow, transform',
    transitionDuration: 'var(--motion-duration-base)',
    transitionTimingFunction: 'var(--motion-ease-apple)',
  },
  header: {
    borderBottomWidth: '1px',
    borderColor: 'borderPrimary',
    pb: 3,
    mb: 3,
  },
}));

// "Filled" varyantı (Chakra'nın varsayılanını temel alıp semantic tokenlarla güncelleyebiliriz)
const filled = definePartsStyle({
    container: {
        bg: "bgSecondary", // semantic token
        borderWidth: "0px", // Genellikle kenarlıksız
        borderColor: "transparent",
    }
});

// "Outline" varyantı
const outline = definePartsStyle({
    container: {
        bg: "transparent", // Veya bgPrimary
        borderWidth: "1px",
        borderColor: "borderSecondary", // Daha belirgin bir kenarlık
    }
});

// ====================================================================
// Faz 2 · Elevation hiyerarsisi (1-5).
// theme/tokens/elevation.js icindeki shadow + surface token'larina baglanir.
// Kullanim: <Card variant="elevation2"> veya <Card variant="elevation4">
// ====================================================================
const makeElevation = (level) => definePartsStyle(() => ({
  container: {
    bg: `surface.${level}`,
    borderRadius: "xl",
    borderWidth: "1px",
    borderColor: "surface.border.subtle",
    boxShadow: `elevation.${level}`,
    overflow: "hidden",
    backgroundClip: "padding-box",
    transitionProperty: "transform, box-shadow, border-color",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
  },
}));

const elevation0 = definePartsStyle(() => ({
  container: {
    bg: "surface.0",
    borderRadius: "xl",
    borderWidth: "1px",
    borderColor: "surface.border.subtle",
    boxShadow: "none",
    overflow: "hidden",
  },
}));

const elevation1 = makeElevation(1);
const elevation2 = makeElevation(2);
const elevation3 = makeElevation(3);
const elevation4 = makeElevation(4);
const elevation5 = makeElevation(5);

// Glow Card (interaktif yukseltme · hover'da glow'a gecer)
const glowBrand = definePartsStyle(() => ({
  container: {
    bg: "surface.2",
    borderRadius: "xl",
    borderWidth: "1px",
    borderColor: "surface.border.accent",
    boxShadow: "elevation.glow.brand",
    overflow: "hidden",
    transitionProperty: "transform, box-shadow",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    _hover: {
      transform: "translateY(-3px)",
      boxShadow: "elevation.4",
    },
  },
}));

// Status Card (sinav sonucu, basari/hata kartlari)
const makeStatusCard = (statusPrefix) => definePartsStyle(() => ({
  container: {
    bg: `${statusPrefix}.bg`,
    borderRadius: "xl",
    borderWidth: "1px",
    borderColor: `${statusPrefix}.border`,
    color: `${statusPrefix}.text`,
    overflow: "hidden",
    transitionProperty: "transform, box-shadow",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
  },
}));

const cardCorrect   = makeStatusCard('exam.correct');
const cardIncorrect = makeStatusCard('exam.incorrect');
const cardReview    = makeStatusCard('exam.review');

// ====================================================================
// Faz 5 · Density bazlı Card boyutları
// <Card size="compact|comfortable|spacious"> · padding + gap + radius
// Tüm sayfalarda tutarlı iç boşluk için merkezi kaynak.
// ====================================================================
const sizes = {
  compact: definePartsStyle({
    container: { borderRadius: 'lg' },
    header: { padding: { base: 3, md: 4 }, paddingBottom: 2 },
    body:   { padding: { base: 3, md: 4 } },
    footer: { padding: { base: 3, md: 4 }, paddingTop: 2 },
  }),
  comfortable: definePartsStyle({
    container: { borderRadius: 'xl' },
    header: { padding: { base: 4, md: 6 }, paddingBottom: 3 },
    body:   { padding: { base: 4, md: 6 } },
    footer: { padding: { base: 4, md: 6 }, paddingTop: 3 },
  }),
  spacious: definePartsStyle({
    container: { borderRadius: 'xl' },
    header: { padding: { base: 6, md: 8 }, paddingBottom: 4 },
    body:   { padding: { base: 6, md: 8 } },
    footer: { padding: { base: 6, md: 8 }, paddingTop: 4 },
  }),
};

export const cardTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants: {
    elevated,
    aiCard,
    glass,
    filled,
    outline,
    // Faz 2 · Elevation hiyerarsisi (1-5)
    elevation0,
    elevation1,
    elevation2,
    elevation3,
    elevation4,
    elevation5,
    // Faz 2 · Glow ve status varyantlari
    glowBrand,
    cardCorrect,
    cardIncorrect,
    cardReview,
    // Chakra'nın diğer varsayılan varyantlarını (unstyled vb.) da ekleyebilirsiniz.
  },
  defaultProps: {
    variant: "glass", // Varsayılan kart varyantı
    size: "comfortable",
  },
});

export default cardTheme; // index.js'de Card: cardTheme olarak kullanılacak
